import { useMutation } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { FC, useEffect } from "react";
import { toast } from "react-toastify";
import {
  Button,
  ButtonVariant,
  Hide,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Input,
  Modal
} from "shared/components";
import { useForm } from "shared/hooks";
import { Money, VerifiedIncomeReport } from "types/landlord/reports";
import { InfoCircle, User03, XClose } from "untitledui-js-base";
import { getErrorMessage } from "utils/error";

type Props = {
  opened: boolean;
  onClose: () => void;
  applicantName: string;
  role: string;
  applicantId: number;
  applicationId: number;
  report?: VerifiedIncomeReport;
  refetchVerification: () => void;
  templateRecommendationSettings?: string;
};

export const EditIncomeModal: FC<Props> = ({
  applicantName,
  opened,
  onClose,
  role,
  applicantId,
  applicationId,
  report,
  refetchVerification,
  templateRecommendationSettings
}) => {
  const { handleChange, onSubmit, register } = useForm({
    gross_monthly_income: "",
    net_monthly_income: ""
  });

  const { mutate: updateVerifiedIncome, isPending } = useMutation({
    mutationFn: (data: {
      gross_monthly_income: Money;
      net_monthly_income: Money;
    }) => {
      return instance.post(
        `/portal/v1/applications/${applicationId}/applicants/${applicantId}/adjust_verified_income`,
        data
      );
    },
    mutationKey: ["EDIT_VERIFIED_INCOME", String(applicantId)],
    onSuccess: () => {
      toast.success(`Verified income was successfully update`);
      refetchVerification();
      onClose();
    },
    onError: (error) => {
      toast.error(`Error updating verified income: ${getErrorMessage(error)}`);
    }
  });
  const { gross_monthly_income, net_monthly_income } = report || {};

  useEffect(() => {
    handleChange(
      "gross_monthly_income",
      () => (gross_monthly_income?.cents || 0) / 100
    )({});
    handleChange(
      "net_monthly_income",
      () => (net_monthly_income?.cents || 0) / 100
    )({});
  }, [gross_monthly_income, net_monthly_income]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      panelClassName="w-[477px] shadow-lg border border-alpha-100"
    >
      <form
        onSubmit={onSubmit({
          onSuccess: (data) => {
            updateVerifiedIncome({
              gross_monthly_income: {
                cents: data.gross_monthly_income * 100,
                currency: "USD"
              },
              net_monthly_income: {
                cents: data.net_monthly_income * 100,
                currency: "USD"
              }
            });
          }
        })}
      >
        <div className="flex items-center justify-between border-b border-alpha-100 p-6">
          <h4 className="text-xl font-bold text-content-1000">
            Edit verified income
          </h4>
          <IconButton
            variant={IconButtonVariant.BASE}
            size={IconButtonSize.SM}
            borderLess
            className="translate-x-2"
            onClick={onClose}
            Icon={XClose}
          />
        </div>
        <Hide if={templateRecommendationSettings === "none"}>
          <div className="bg-warning-50 px-6 py-4 flex gap-4 border-b border-alpha-100 items-center">
            <div>
              <InfoCircle size="24" className="stroke-warning-700" />
            </div>
            <p className="text-warning-700 font-medium text-sm">
              Please note that editing this income will lead to an application
              score recalculation. Decision recommendations may change.
            </p>
          </div>
        </Hide>
        <div className="py-5 px-6 flex gap-4 border-b border-alpha-100">
          <div className="bg-primary-20 rounded-full p-3">
            <User03 size="20" className="stroke-primary-500" />
          </div>
          <div>
            <p className="text-base text-content-1000 font-medium">
              {applicantName}
            </p>
            <p className="text-xs text-content-400 font-medium">{role}</p>
          </div>
        </div>
        <div className="p-6 bg-background-900 border-b border-alpha-100">
          <Input
            id="gross_monthly_income"
            type="money"
            label="Gross monthly income"
            className="mb-4"
            hideOptional
            {...register("gross_monthly_income")}
          />
          <Input
            id="net_monthly_income"
            type="money"
            label="Net monthly income"
            hideOptional
            {...register("net_monthly_income")}
          />
        </div>
        <div className="py-4 px-6 flex gap-2 justify-end">
          <Button className="w-[125px]" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            type="submit"
            loading={isPending}
          >
            Save changes
          </Button>
        </div>
      </form>
    </Modal>
  );
};
